import {
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    ScrollView,
    View
} from "react-native";
import type {
    FilterStateType
} from "./type";
import stylesheet from "./stylesheet";
import MemberCard from "./components/memberCard";
import MemberFilters from "./components/memberFilters";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useFocusEffect
} from "@react-navigation/native";
import type {
    GetAllUsersForAdminObjectType
} from "../../../../services/restAPI/actions/getAllUsersForAdmin/type";
import type {
    GetSectorsResponseType
} from "../../../../services/restAPI/actions/getSectors/type";
import {
    KeyRoundIcon,
    PlusIcon,
    BellIcon,
    MailIcon
} from "lucide-react-native";
import SendNotificationModal from "../../../notifications/views/sendNotificationModal";
import SendMailModal from "../../../notifications/views/sendMailModal";
import RESTServiceContext from "../../../../services/restAPI/context";
import Pagination from "../../../../components/pagination";
import PasswordResetModal from "../passwordResetModal";
import MemberCreateModal from "../createModal";

const MembersList = () => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 992;

    const [
        members,
        setMembers
    ] = useState<GetAllUsersForAdminObjectType[]>([]);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        sectors,
        setSectors
    ] = useState<GetSectorsResponseType["payload"]>([]);

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        filters,
        setFilters
    ] = useState<FilterStateType>({
        sectorIDs: [],
        status: []
    });

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        isCreateModalVisible,
        setIsCreateModalVisible
    ] = useState<boolean>(false);

    const [
        isPasswordResetModalVisible,
        setIsPasswordResetModalVisible
    ] = useState<boolean>(false);

    const [
        isSendNotificationModalVisible,
        setIsSendNotificationModalVisible
    ] = useState<boolean>(false);

    const [
        isSendMailModalVisible,
        setIsSendMailModalVisible
    ] = useState<boolean>(false);

    const scrollViewRef = useRef<ScrollView>(null);

    const [
        debouncedSearchQuery,
        setDebouncedSearchQuery
    ] = useState<string>("");

    const [
        debouncedFilters,
        setDebouncedFilters
    ] = useState<FilterStateType>({
        sectorIDs: [],
        status: []
    });

    const fetchMembers = async (currentPage: number, search: string, currentFilters: FilterStateType) => {
        try {
            setLoading(true);
            setMembers([]);

            const requestParams: Record<string, string | string[] | number | boolean> = {
                page: currentPage
            };

            if (currentFilters.status.includes("isStudent")) {
                requestParams.isStudent = true;
            }
            if (currentFilters.status.includes("isBlacklisted")) {
                requestParams.isBlacklisted = true;
            }
            if (currentFilters.status.includes("isDeleted")) {
                requestParams.isDeleted = true;
            }

            if (currentFilters.sectorIDs && currentFilters.sectorIDs.length > 0) {
                requestParams.sectorIDs = currentFilters.sectorIDs;
            }

            if (search) {
                requestParams.search = search;
            }

            const result = await RESTServiceContext.action("getAllUsersForAdmin", requestParams);

            if (result.payload) {
                setMembers(result.payload);
                if (result.totalCount !== undefined) {
                    setTotalCount(result.totalCount);
                }
            } else {
                setMembers([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilters(prev => {
                const isSameSector = prev.sectorIDs.length === filters.sectorIDs.length && prev.sectorIDs.every((v, i) => v === filters.sectorIDs[i]);
                const isSameStatus = prev.status.length === filters.status.length && prev.status.every((v, i) => v === filters.status[i]);
                if (isSameSector && isSameStatus) {
                    return prev;
                }
                return filters;
            });
            setDebouncedSearchQuery(prev => prev !== searchQuery ? searchQuery : prev);
        }, 500);

        return () => clearTimeout(timer);
    }, [
        searchQuery,
        filters
    ]);

    useFocusEffect(
        useCallback(() => {
            fetchMembers(1, debouncedSearchQuery, debouncedFilters);
            setPage(1);
        }, [
            debouncedSearchQuery,
            debouncedFilters
        ])
    );

    useEffect(() => {
        const fetchSectors = async () => {
            try {
                const result = await RESTServiceContext.action("getSectors", null);
                if (result.payload) {
                    setSectors(result.payload);
                }
            } catch (error) {
                console.error(error);
            }
        };
        fetchSectors();
    }, []);

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Text
                variant="titleLargeSize"
            >
                {localize("members-page-title")}
            </Text>
            <View
                style={[
                    stylesheet.headerActions,
                    {
                        width: isMobile ? "100%" : undefined,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    onPress={() => setIsSendNotificationModalVisible(true)}
                    title={localize("notifications-send-notification")}
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <BellIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    variant="outline"
                />
                <Button
                    onPress={() => setIsSendMailModalVisible(true)}
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                    customTextColor={colors.project.brandWhite}
                    title={localize("notifications-send-mail")}
                    icon={({
                        size
                    }) => <MailIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    variant="outline"
                />
                <Button
                    onPress={() => setIsPasswordResetModalVisible(true)}
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <KeyRoundIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    title="Şifre Sıfırlama Gönder"
                    customColor="secondaryButton"
                    variant="outline"
                />
                <Button
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                    onPress={() => setIsCreateModalVisible(true)}
                    icon={({
                        color,
                        size
                    }) => <PlusIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    title={localize("members-create-new")}
                    customColor="primary"
                    variant="filled"
                />
            </View>
        </View>;
    };

    const renderTableHeader = () => {
        if (isMobile) {
            return null;
        }

        return <View
            style={[
                stylesheet.tableHeaderRow,
                {
                    borderBottomColor: colors.project.brandBorder,
                    borderBottomWidth: borders.seperator,
                    paddingVertical: spaces.spacingMd,
                    marginBottom: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.colUser,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    style={[
                        stylesheet.tableHeaderText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("members-table-user")}
                </Text>
            </View>
            <View
                style={stylesheet.colContact}
            >
                <Text
                    style={[
                        stylesheet.tableHeaderText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("members-table-company")}
                </Text>
            </View>
            <View
                style={stylesheet.colSector}
            >
                <Text
                    style={[
                        stylesheet.tableHeaderText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("members-table-sector")}
                </Text>
            </View>
            <View
                style={stylesheet.colDate}
            >
                <Text
                    style={[
                        stylesheet.tableHeaderText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("members-table-date")}
                </Text>
            </View>
            <View
                style={stylesheet.colStatus}
            >
                <Text
                    style={[
                        stylesheet.tableHeaderText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("members-table-status")}
                </Text>
            </View>
            <View
                style={stylesheet.colActions}
            />
        </View>;
    };

    const renderContent = () => {
        if (loading) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl,
                        flex: 1
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>;
        }

        if (members.length === 0) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl,
                        flex: 1
                    }
                ]}
            >
                <Text
                    style={[
                        stylesheet.emptyText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyLargeSize"
                >
                    {localize("members-empty-list")}
                </Text>
            </View>;
        }

        return <View
            style={{
                flex: 1
            }}
        >
            <ScrollView
                contentContainerStyle={{
                    paddingBottom: spaces.spacingXl,
                    flexGrow: 1
                }}
                ref={scrollViewRef}
            >
                {renderTableHeader()}
                {members.map(item => <MemberCard
                    key={item._id || `member-${item._id}`}
                    sectors={sectors}
                    item={item}
                />)}
                <View
                    style={{
                        marginTop: spaces.spacingXl
                    }}
                >
                    <Pagination
                        onSelect={(item) => {
                            setPage(item.pageNumber);
                            if (scrollViewRef.current) {
                                scrollViewRef.current.scrollTo({
                                    animated: false,
                                    y: 0
                                });
                            }
                            fetchMembers(item.pageNumber, debouncedSearchQuery, debouncedFilters);
                        }}
                        totalDataCount={totalCount}
                        currentPage={page}
                        itemPerPage={20}
                    />
                </View>
            </ScrollView>
        </View>;
    };

    return <PageContainer
        isScrollable={false}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                    gap: spaces.spacingLg,
                    flex: 1
                }
            ]}
        >
            {renderHeader()}
            <MemberFilters
                setSearchQuery={setSearchQuery}
                searchQuery={searchQuery}
                setFilters={setFilters}
                sectors={sectors}
                filters={filters}
            />
            {renderContent()}
        </View>
        <MemberCreateModal
            onSuccess={() => fetchMembers(1, debouncedSearchQuery, debouncedFilters)}
            onClose={() => setIsCreateModalVisible(false)}
            isVisible={isCreateModalVisible}
        />
        <PasswordResetModal
            onClose={() => setIsPasswordResetModalVisible(false)}
            isVisible={isPasswordResetModalVisible}
        />
        <SendNotificationModal
            onSuccess={() => fetchMembers(1, debouncedSearchQuery, debouncedFilters)}
            onClose={() => setIsSendNotificationModalVisible(false)}
            isVisible={isSendNotificationModalVisible}
        />
        <SendMailModal
            onSuccess={() => fetchMembers(1, debouncedSearchQuery, debouncedFilters)}
            onClose={() => setIsSendMailModalVisible(false)}
            isVisible={isSendMailModalVisible}
        />
    </PageContainer>;
};

export default MembersList;
